<template>
  <!--
    Standardized people picker. v-model is an array of { id, name } chips:
    id links to a known contributor; a typed name with id=null is free-text.
    Searches the contributors endpoint so you pick from people you've worked
    with instead of retyping. Mirrors the original PortfolioView image-people picker.
  -->
  <div class="people-picker">
    <span v-for="(p, pi) in modelValue" :key="'p-' + pi" class="people-chip">
      {{ p.name }}
      <span v-if="p.id" class="chip-known" title="Linked to a known person">✓</span>
      <button type="button" class="chip-x" @click="removeAt(pi)" title="Remove">✕</button>
    </span>
    <div class="people-input-wrap">
      <input
        v-model="query"
        :placeholder="modelValue.length ? 'Add another…' : 'Type a name…'"
        @input="onSearch"
        @focus="open = true; onSearch()"
        @keydown.enter.prevent="addTyped"
        @blur="blur"
      />
      <ul v-if="open && (results.length || query.trim())" class="contrib-suggest">
        <li v-for="r in results" :key="r.id" @mousedown.prevent="pick(r)">
          <strong>{{ r.name }}</strong>
          <span class="contrib-meta">
            <template v-if="r.business_name">{{ r.business_name }} · </template>
            {{ r.shoot_count }} shoot{{ r.shoot_count === 1 ? '' : 's' }}
          </span>
        </li>
        <li
          v-if="query.trim() && !results.some(r => r.name.toLowerCase() === query.trim().toLowerCase())"
          @mousedown.prevent="addTyped"
          class="people-add-new"
        >
          <strong>+ Add “{{ query.trim() }}”</strong>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useAdminApi } from '@/composables/useAdminApi'

type Person = { id: number | null; name: string }

const props = withDefaults(defineProps<{
  modelValue: Person[]
  searchUrl?: string
}>(), { searchUrl: '/admin/portfolio/contributors' })

const emit = defineEmits<{ (e: 'update:modelValue', v: Person[]): void }>()

const { apiFetch } = useAdminApi()

const query = ref('')
const open = ref(false)
const results = ref<any[]>([])
let searchTimer: any = null

function onSearch() {
  open.value = true
  const q = query.value.trim()
  clearTimeout(searchTimer)
  searchTimer = setTimeout(async () => {
    try {
      const res = await apiFetch(`${props.searchUrl}?q=${encodeURIComponent(q)}`)
      const list = (res.ok ? (await res.json()).contributors : []) || []
      const have = new Set(props.modelValue.map(p => (p.id ? 'id:' + p.id : 'n:' + p.name.toLowerCase())))
      results.value = list.filter((r: any) => !have.has('id:' + r.id))
    } catch { results.value = [] }
  }, 200)
}
function pick(r: any) {
  emit('update:modelValue', [...props.modelValue, { id: r.id, name: r.name }])
  query.value = ''
  results.value = []
}
function addTyped() {
  const name = query.value.trim()
  if (!name) return
  if (!props.modelValue.some(p => p.name.toLowerCase() === name.toLowerCase())) {
    emit('update:modelValue', [...props.modelValue, { id: null, name }])
  }
  query.value = ''
  results.value = []
}
function removeAt(i: number) {
  const next = [...props.modelValue]
  next.splice(i, 1)
  emit('update:modelValue', next)
}
function blur() {
  setTimeout(() => {
    // Fold a still-typed name into chips so it isn't lost when the field loses focus.
    if (query.value.trim()) addTyped()
    open.value = false
  }, 150)
}
</script>

<style scoped>
.people-picker {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--bg-input);
}
.people-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.15rem 0.45rem;
  background: var(--bg-surface-hover);
  border-radius: 999px;
  font-size: 0.78rem;
  color: var(--text-primary);
}
.chip-known { color: var(--status-converted-text); font-size: 0.72rem; }
.chip-x {
  background: none; border: none; cursor: pointer;
  color: var(--text-muted); font-size: 0.72rem; line-height: 1; padding: 0 0.1rem;
}
.chip-x:hover { color: var(--danger-text); }
.people-input-wrap { position: relative; flex: 1; min-width: 120px; }
.people-input-wrap input {
  width: 100%;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.85rem;
  padding: 0.2rem 0.1rem;
}
.people-input-wrap input:focus { outline: none; }
.contrib-suggest {
  position: absolute;
  z-index: 20;
  top: calc(100% + 2px);
  left: 0; right: 0;
  margin: 0; padding: 0.25rem 0;
  list-style: none;
  background: var(--bg-elevated, var(--bg-surface));
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  max-height: 240px;
  overflow-y: auto;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.55);
}
.contrib-suggest li {
  padding: 0.4rem 0.7rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.contrib-suggest li:hover { background: var(--bg-surface-hover); }
.contrib-suggest strong { color: var(--text-primary); font-size: 0.85rem; }
.contrib-meta { color: var(--text-muted); font-size: 0.72rem; }
.people-add-new strong { color: var(--accent, var(--status-converted-text)); }
</style>
